import { createContext, useState } from 'react';
import { AUTH_TOKEN } from '../configs/AppConfig';
import {
  DIR_LTR,
  NAV_TYPE_SIDE,
  NAV_TYPE_TOP,
  THEME_TYPE_LIGHT,
} from '../constants/ThemeConstants';
import { MENU_ITEMS, PERMISSION_METRICS } from '../redux/constants/Auth';

/**
 * @typedef {Object} RootContextType
 * @property {string|null} token - Authentication token.
 * @property {string|null} p_m__ - Authentication p_m__.
 * @property {string|null} sideMenu - Side menu object
 * @property {string} themeColor - Theme color.
 * @property {string} theme - Theme type (light or dark).
 * @property {boolean} sideNavCollapsed - State of side navigation collapse.
 * @property {string} navType - Navigation type (side or top).
 * @property {string} direction - Text direction (LTR or RTL).
 * @property {boolean} mobileNav - State of mobile navigation collapse.
 * @property {Function} changeToken - Function to change the authentication token.
 * @property {Function} changePermission - Function to change the authentication permission.
 * @property {Function} changeSideNavState - Function to toggle the state of side navigation collapse.
 * @property {Function} changeTheme - Function to change the theme type.
 * @property {Function} changeNavType - Function to change the navigation type.
 * @property {Function} changeDirection - Function to change the text direction.
 * @property {Function} changeMobileNavState - Function to toggle the state of mobile navigation collapse.
 */

/**
 * The context that provides the state and actions for the root components of the application.
 * @type {React.Context<RootContextType>}
 */
export const RootContext = createContext({
  token: null,
  userDetails: null,
  p_m__: null,
  sideMenu: null,
  themeColor: '',
  theme: THEME_TYPE_LIGHT,
  sideNavCollapsed: false,
  navType: NAV_TYPE_TOP,
  direction: DIR_LTR,
  mobileNav: false,
  userList: [],
  custProgramList: [],
  appCode: null,
  changeToken: () => {},
  changeUserDetails: () => {},
  changePermission: () => {},
  changeSideMenu: () => {},
  changeSideNavState: () => {},
  changeTheme: () => {},
  changeNavType: () => {},
  changeDirection: () => {},
  changeMobileNavState: () => {},
  changeUserList: () => {},
  changeCustProgramList: () => {},
  changeAppCode: () => {},
});

/**
 * Provides the RootContext to its children.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components that will have access to the context.
 * @returns {JSX.Element} The provider component that wraps the children with the RootContext.
 */
export default function RootContextProvider({ children }) {
  const [value, setValue] = useState(localStorage.getItem(AUTH_TOKEN) || null);
  const [user, setUser] = useState();
  const [permission, setPermission] = useState(
    localStorage.getItem(PERMISSION_METRICS) || null
  );
  const [sideMenu, setSideMenu] = useState(
    JSON.parse(localStorage.getItem(MENU_ITEMS)) || []
  );

  const [sideNavCollapsed, setSideNavCollapsed] = useState(false);
  const [navType, setNavType] = useState(
    localStorage.getItem('navtype') || NAV_TYPE_TOP
  );
  const [theme, setTheme] = useState(
    localStorage.getItem('theme') || THEME_TYPE_LIGHT
  );
  const [direction, setDirection] = useState(DIR_LTR);
  const [mobileNavCollapsed, setMobileNavCollapsed] = useState(false);
  const [userList, setUserList] = useState([]);
  const [custProgramList, setCustProgramList] = useState([]);
  const [appCode, setAppCode] = useState(null);
  const ctx = {
    token: value,
    userDetails: user,
    p_m__: permission,
    sideMenu: sideMenu,
    theme: theme,
    sideNavCollapsed: sideNavCollapsed,
    navType: navType,
    direction: direction,
    mobileNav: mobileNavCollapsed,
    userList: userList,
    custProgramList: custProgramList,
    appCode: appCode,
    changeToken: changeToken,
    changeUserDetails: changeUserDetails,
    changePermission: changePermission,
    changeSideMenu: changeSideMenu,
    changeSideNavState: changeSideNavState,
    changeNavType: changeNavType,
    changeTheme: changeTheme,
    changeDirection: changeDirection,
    changeMobileNavState: changeMobileNavState,
    changeUserList: changeUserList,
    changeCustProgramList: changeCustProgramList,
    changeAppCode: changeAppCode,
  };

  /**
   * Updates the authentication token.
   *
   * @param {string|null} e - The new token value.
   */
  function changeToken(e) {
    setValue(e);
  }

  function changeUserDetails(e) {
    setUser(e)
  }

  function changePermission(e) {
    setPermission(e);
  }

  function changeSideMenu(e) {
    setSideMenu(e);
  }

  /**
   * Toggles the side navigation collapsed state.
   */
  function changeSideNavState() {
    setSideNavCollapsed((prev) => !prev);
  }

  /**
   * Updates the navigation type and saves it to localStorage.
   *
   * @param {string} e - The new navigation type.
   */
  function changeNavType(e) {
    setNavType(e);
    localStorage.setItem('navtype', e);
  }

  /**
   * Updates the theme type and saves it to localStorage.
   *
   * @param {string} e - The new theme type.
   */
  function changeTheme(e) {
    setTheme(e);
    localStorage.setItem('theme', e);
  }

  /**
   * Updates the text direction.
   *
   * @param {string} e - The new text direction (LTR or RTL).
   */
  function changeDirection(e) {
    setDirection(e);
  }

  /**
   * Toggles the mobile navigation collapsed state.
   */
  function changeMobileNavState() {
    setMobileNavCollapsed((prev) => !prev);
  }

  function changeUserList(e) {
    setUserList(e)
  }
  
  function changeCustProgramList(e) {
    setCustProgramList(e)
  }

  function changeAppCode(e){
    setAppCode(e)
  }
  

  return <RootContext.Provider value={ctx}>{children}</RootContext.Provider>;
}
